import React, { useState } from 'react';
import { jsPDF } from 'jspdf';

/**
 * ArReportDocument
 * A4-style financial report — preview in-browser, download as native vector PDF via jsPDF.
 * Props:
 *   data   - { metrics, agingAnalysis, customers, ebitda: { total, net_income, addbacks[] } }
 *   period - { startDate, endDate, filterType }
 *   onClose - fn to close the overlay
 */

const fmt  = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;

// Hex → [r, g, b]
const hexRgb = (hex) => {
    const h = hex.replace('#', '');
    return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
};

const AGING_COLORS = {
    'Current': '#22c55e',
    '1-10': '#a3e635',
    '11-15': '#facc15',
    '16-30': '#fb923c',
    '31-45': '#f97316',
    '46-60': '#ef4444',
    '61-90': '#dc2626',
    '91+': '#991b1b',
};

// ─────────────────────────────────────────────────────────────────────────────
// PDF generator — pure jsPDF, no images, vector output
// ─────────────────────────────────────────────────────────────────────────────
const generatePDF = ({ metrics, agingAnalysis, customers, ebitda, period, agingEntries }) => {
    return new Promise((resolve) => {
        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

        const PW  = doc.internal.pageSize.getWidth();   // 595.28
        const PH  = doc.internal.pageSize.getHeight();  // 841.89
        const ML  = 40;   // margin left
        const MR  = 40;   // margin right
        const CW  = PW - ML - MR;  // content width
        let   y   = 40;   // current Y cursor

        const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const periodLabel = period?.startDate && period?.endDate
            ? `${period.startDate} — ${period.endDate}`
            : 'All Time';

        // ── helpers ──────────────────────────────────────────────────────────
        const setFont = (style = 'normal', size = 10, color = '#1e293b') => {
            doc.setFont('helvetica', style);
            doc.setFontSize(size);
            const [r, g, b] = hexRgb(color);
            doc.setTextColor(r, g, b);
        };

        const fillRect = (x, ry, w, h, color) => {
            const [r, g, b] = hexRgb(color);
            doc.setFillColor(r, g, b);
            doc.rect(x, ry, w, h, 'F');
        };

        const strokeLine = (x1, y1, x2, y2, color = '#e2e8f0', lw = 0.5) => {
            const [r, g, b] = hexRgb(color);
            doc.setDrawColor(r, g, b);
            doc.setLineWidth(lw);
            doc.line(x1, y1, x2, y2);
        };

        /** Ensure at least `needed` pts remain on page; add new page if not. */
        const ensureSpace = (needed) => {
            if (y + needed > PH - 40) {
                doc.addPage();
                y = 40;
            }
        };

        /** Section label row */
        const sectionTitle = (title) => {
            ensureSpace(22);
            setFont('bold', 7.5, '#64748b');
            doc.text(title.toUpperCase(), ML, y);
            y += 3;
            strokeLine(ML, y, ML + CW, y, '#e2e8f0', 0.5);
            y += 10;
        };

        // ── HEADER ───────────────────────────────────────────────────────────
        setFont('bold', 16, '#0f172a');
        doc.text('Accounts Receivable Report', ML, y);

        setFont('normal', 8.5, '#475569');
        doc.text(`Generated: ${today}`, PW - MR, y, { align: 'right' });
        setFont('italic', 7.5, '#94a3b8');
        doc.text('Powered by Golag AI', PW - MR, y + 11, { align: 'right' });

        y += 16;
        setFont('normal', 8.5, '#64748b');
        doc.text(`Period: ${periodLabel}   ·   Filter: ${period?.filterType || 'Created'}`, ML, y);
        y += 10;
        strokeLine(ML, y, ML + CW, y, '#1e293b', 1.5);
        y += 20;

        // ── KEY METRICS (3-column grid) ───────────────────────────────────────
        sectionTitle('Key Metrics');

        const metricCards = [
            { label: 'Total AR Outstanding',    value: fmt(metrics.totalAR) },
            { label: 'Collection Efficiency',   value: fmtPct(metrics.collectionEfficiency) },
            { label: 'Cash Realization',        value: fmtPct(metrics.cashRealization) },
            { label: 'Days Sales Outstanding',  value: `${(metrics.daysSalesOutstanding || 0).toFixed(1)}d` },
            { label: 'Avg Trust Score',         value: `${(metrics.avgTrustScore || 0).toFixed(1)}/100` },
            { label: 'At-Risk Accounts',        value: String(metrics.atRiskAccounts ?? 0) },
        ];

        const cols   = 3;
        const gap    = 8;
        const cardW  = (CW - gap * (cols - 1)) / cols;
        const cardH  = 44;
        ensureSpace(cardH * Math.ceil(metricCards.length / cols) + 10);

        metricCards.forEach((m, i) => {
            const col = i % cols;
            const row = Math.floor(i / cols);
            const cx  = ML + col * (cardW + gap);
            const cy  = y + row * (cardH + gap);

            fillRect(cx, cy, cardW, cardH, '#f8fafc');
            const [rb, gb, bb] = hexRgb('#e2e8f0');
            doc.setDrawColor(rb, gb, bb);
            doc.setLineWidth(0.5);
            doc.rect(cx, cy, cardW, cardH, 'S');

            setFont('bold', 6.5, '#94a3b8');
            doc.text(m.label.toUpperCase(), cx + 8, cy + 13);
            setFont('bold', 13, '#0f172a');
            doc.text(m.value, cx + 8, cy + 32);
        });

        y += Math.ceil(metricCards.length / cols) * (cardH + gap) + 8;

        // ── AR AGING ANALYSIS ─────────────────────────────────────────────────
        sectionTitle('AR Aging Analysis');

        // Column widths: bucket | amount | % | trust | bar
        const aCols = [90, 90, 58, 72, 0]; // last col fills remainder
        aCols[4] = CW - aCols.slice(0, 4).reduce((s,v) => s+v, 0);
        const aRowH = 18;

        // Header row
        ensureSpace(aRowH + 2);
        fillRect(ML, y, CW, aRowH, '#f1f5f9');
        setFont('bold', 7, '#64748b');
        const aHeaders = ['Aging Bucket', 'Amount', '% of Total AR', 'Avg Trust Score', 'Bar'];
        const aAligns  = ['left', 'right', 'right', 'right', 'left'];
        let ax = ML;
        aHeaders.forEach((h, i) => {
            const tw = aCols[i];
            const tx = aAligns[i] === 'right' ? ax + tw - 4 : ax + 4;
            doc.text(h, tx, y + 12, { align: aAligns[i] === 'right' ? 'right' : 'left' });
            ax += tw;
        });
        y += aRowH;
        strokeLine(ML, y, ML + CW, y);

        // Data rows
        agingEntries.forEach((row, i) => {
            ensureSpace(aRowH + 2);
            fillRect(ML, y, CW, aRowH, i % 2 === 0 ? '#ffffff' : '#f8fafc');
            strokeLine(ML, y + aRowH, ML + CW, y + aRowH);

            const cells = [
                row.label,
                fmt(row.amount),
                `${row.pct}%`,
                row.avgTrustScore !== null && row.amount > 0 ? Number(row.avgTrustScore).toFixed(1) : '—',
            ];
            const cellAligns = ['left', 'right', 'right', 'right'];

            ax = ML;
            cells.forEach((val, ci) => {
                setFont(ci === 0 ? 'bold' : 'normal', 8, ci === 0 ? '#0f172a' : '#374151');
                const tw = aCols[ci];
                const tx = cellAligns[ci] === 'right' ? ax + tw - 4 : ax + 6;
                doc.text(String(val), tx, y + 12, { align: cellAligns[ci] === 'right' ? 'right' : 'left' });
                ax += tw;
            });

            // Progress bar
            const barX = ML + aCols[0] + aCols[1] + aCols[2] + aCols[3] + 6;
            const barW = aCols[4] - 12;
            const barH = 5;
            const barY = y + (aRowH - barH) / 2;
            fillRect(barX, barY, barW, barH, '#e2e8f0');
            const pct = Math.min(parseFloat(row.pct) || 0, 100);
            if (pct > 0) {
                fillRect(barX, barY, barW * (pct / 100), barH, AGING_COLORS[row.label] || '#3b82f6');
            }

            y += aRowH;
        });

        // Total row
        ensureSpace(aRowH + 6);
        fillRect(ML, y, CW, aRowH, '#1e293b');
        setFont('bold', 8.5, '#f1f5f9');
        doc.text('Total AR', ML + 6, y + 12);
        doc.text(fmt(metrics.totalAR), ML + aCols[0] + aCols[1] - 4, y + 12, { align: 'right' });
        setFont('normal', 7.5, '#94a3b8');
        doc.text('100%', ML + aCols[0] + aCols[1] + aCols[2] - 4, y + 12, { align: 'right' });
        y += aRowH + 16;

        // ── CUSTOMER AR OVERVIEW ──────────────────────────────────────────────
        sectionTitle('Customer AR Overview');

        const cCols   = [140, 75, 60, 50, 55, 55, 70]; // customer | outstanding | trust | terms | due | status | last invoice
        const cHeaders = ['Customer', 'Outstanding', 'Trust Score', 'Terms', 'Due Date', 'Status', 'Last Invoice'];
        const cAligns  = ['left', 'right', 'right', 'center', 'right', 'center', 'right'];
        const cRowH   = 17;

        ensureSpace(cRowH + 2);
        fillRect(ML, y, CW, cRowH, '#f1f5f9');
        setFont('bold', 7, '#64748b');
        ax = ML;
        cHeaders.forEach((h, i) => {
            const tw = cCols[i];
            let tx;
            if (cAligns[i] === 'right')  tx = ax + tw - 3;
            else if (cAligns[i] === 'center') tx = ax + tw / 2;
            else tx = ax + 4;
            doc.text(h, tx, y + 11, { align: cAligns[i] === 'right' ? 'right' : cAligns[i] === 'center' ? 'center' : 'left' });
            ax += tw;
        });
        y += cRowH;
        strokeLine(ML, y, ML + CW, y);

        (customers || []).slice(0, 30).forEach((c, i) => {
            ensureSpace(cRowH + 2);
            fillRect(ML, y, CW, cRowH, i % 2 === 0 ? '#ffffff' : '#f8fafc');
            strokeLine(ML, y + cRowH, ML + CW, y + cRowH, '#e2e8f0', 0.4);

            const isOverdue = c.status === 'Overdue';

            const cells = [
                c.customer || '—',
                fmt(c.outstanding),
                `${(c.trustScore || 0).toFixed(1)}`,
                c.terms || '—',
                c.dueDate ? new Date(c.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: '2-digit' }) : '—',
                c.status || '—',
                c.lastInvoiceDate ? new Date(c.lastInvoiceDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: '2-digit' }) : '—',
            ];

            ax = ML;
            cells.forEach((val, ci) => {
                const tw = cCols[ci];
                let tx;
                if (cAligns[ci] === 'right')   tx = ax + tw - 3;
                else if (cAligns[ci] === 'center') tx = ax + tw / 2;
                else tx = ax + 4;

                // Colour-code customer name, status, trust score
                let color = '#374151';
                if (ci === 0) color = c.isAtRisk ? '#b91c1c' : '#1e40af';
                if (ci === 2) color = (c.trustScore || 0) > 85 ? '#15803d' : (c.trustScore || 0) > 65 ? '#a16207' : '#b91c1c';
                if (ci === 5) color = isOverdue ? '#b91c1c' : '#15803d';

                setFont(ci === 0 ? 'bold' : 'normal', 7.5, color);
                // Truncate long customer names
                const maxW = tw - 6;
                const displayVal = ci === 0 ? doc.splitTextToSize((c.isAtRisk ? '! ' : '') + val, maxW)[0] : val;
                doc.text(displayVal, tx, y + 11.5, { align: cAligns[ci] === 'right' ? 'right' : cAligns[ci] === 'center' ? 'center' : 'left' });

                // +N badge next to status
                if (ci === 5 && isOverdue && c.overdueCount > 1) {
                    const badgeLabel = `+${c.overdueCount - 1}`;
                    const badgeX = ax + tw / 2 + doc.getTextWidth(val) / 2 + 2;
                    setFont('bold', 6.5, '#991b1b');
                    doc.text(badgeLabel, badgeX, y + 11.5);
                }

                ax += tw;
            });
            y += cRowH;
        });

        if ((customers || []).length > 30) {
            ensureSpace(16);
            setFont('italic', 7.5, '#94a3b8');
            doc.text(`+ ${customers.length - 30} more customers not shown`, ML + CW / 2, y + 10, { align: 'center' });
            y += 16;
        }

        y += 16;

        // ── EBITDA BREAKDOWN ──────────────────────────────────────────────────
        sectionTitle('EBITDA Breakdown — From P&L Report');

        const eRowH = 18;
        const breakdown = ebitda?.net_income_breakdown || [];
        const addbacks  = ebitda?.addbacks || [];

        if (breakdown.length === 0) {
            ensureSpace(eRowH);
            setFont('italic', 8, '#9ca3af');
            doc.text('No P&L breakdown data available for this period.', ML + 6, y + 12);
            y += eRowH;
        } else {
            breakdown.forEach((item, i) => {
                ensureSpace(eRowH + 2);
                const isSubtotal = item.type === 'subtotal';
                const isExpense  = item.type === 'expense';
                fillRect(ML, y, CW, eRowH, isSubtotal ? '#f1f5f9' : i % 2 === 0 ? '#ffffff' : '#fafafa');
                strokeLine(ML, y + eRowH, ML + CW, y + eRowH, '#e2e8f0', 0.4);

                setFont(isSubtotal ? 'bold' : 'normal', isSubtotal ? 8.5 : 8, isSubtotal ? '#374151' : '#6b7280');
                const prefix = isSubtotal ? '' : (isExpense ? '-  ' : '+  ');
                doc.text(prefix + item.name, ML + (isSubtotal ? 6 : 18), y + 12);

                const amtColor = isExpense ? '#b91c1c' : (item.type === 'income' ? '#15803d' : '#374151');
                setFont(isSubtotal ? 'bold' : 'normal', isSubtotal ? 8.5 : 8, amtColor);
                const amtStr = isExpense ? `(${fmt(Math.abs(item.amount))})` : fmt(Math.abs(item.amount));
                doc.text(amtStr, ML + CW - 6, y + 12, { align: 'right' });

                y += eRowH;
            });
        }

        // Net Income row
        ensureSpace(eRowH + 4);
        fillRect(ML, y, CW, eRowH, '#f0fdf4');
        strokeLine(ML, y + eRowH, ML + CW, y + eRowH, '#e2e8f0', 1);
        setFont('bold', 9, '#374151');
        doc.text('Net Income', ML + 6, y + 12);
        const netColor = (ebitda?.net_income || 0) >= 0 ? '#15803d' : '#b91c1c';
        setFont('bold', 9, netColor);
        doc.text(fmt(ebitda?.net_income), ML + CW - 6, y + 12, { align: 'right' });
        y += eRowH + 4;

        if (addbacks.length === 0) {
            ensureSpace(eRowH);
            setFont('italic', 8, '#9ca3af');
            doc.text('No depreciation / amortization / interest accounts identified.', ML + 6, y + 12);
            y += eRowH;
        } else {
            addbacks.forEach((ab, i) => {
                ensureSpace(eRowH + 2);
                fillRect(ML, y, CW, eRowH, i % 2 === 0 ? '#ffffff' : '#f8fafc');
                strokeLine(ML, y + eRowH, ML + CW, y + eRowH, '#e2e8f0', 0.4);
                setFont('normal', 8, '#6b7280');
                doc.text('+  ' + ab.name, ML + 18, y + 12);
                setFont('normal', 8, '#374151');
                doc.text(fmt(ab.amount), ML + CW - 6, y + 12, { align: 'right' });
                y += eRowH;
            });
        }

        // EBITDA Total row
        ensureSpace(28);
        fillRect(ML, y, CW, 26, '#1e293b');
        setFont('bold', 10, '#f1f5f9');
        doc.text('EBITDA Total', ML + 8, y + 17);
        const ebitdaColor = (ebitda?.total || 0) >= 0 ? '#4ade80' : '#f87171';
        setFont('bold', 13, ebitdaColor);
        doc.text(fmt(ebitda?.total), ML + CW - 8, y + 17, { align: 'right' });
        y += 36;

        // ── FOOTER ────────────────────────────────────────────────────────────
        const footerY = PH - 28;
        strokeLine(ML, footerY, ML + CW, footerY, '#e2e8f0', 0.5);
        setFont('normal', 7, '#94a3b8');
        doc.text('This report is generated from QuickBooks Online data via Golag AI.', ML, footerY + 12);
        doc.text(today, ML + CW, footerY + 12, { align: 'right' });

        // Page numbers
        const pageCount = doc.internal.getNumberOfPages();
        for (let p = 1; p <= pageCount; p++) {
            doc.setPage(p);
            setFont('normal', 7, '#94a3b8');
            doc.text(`Page ${p} of ${pageCount}`, PW / 2, PH - 14, { align: 'center' });
        }

        const filterLabel = (period?.filterType || 'created').toLowerCase().replace(/\s+/g, '_');
        const dateFrom    = period?.startDate || 'all';
        const dateTo      = period?.endDate   || 'time';
        doc.save(`AR_Report_${dateFrom}_to_${dateTo}_by_${filterLabel}.pdf`);
        resolve();
    });
};

const ArReportDocument = ({ data, period, onClose }) => {
    const [isGenerating, setIsGenerating] = useState(false);


    const handlePrint = async () => {
        if (!data || !data.metrics) return;
        setIsGenerating(true);
        try {
            await generatePDF({ metrics, agingAnalysis, customers, ebitda, period, agingEntries });
        } finally {
            setIsGenerating(false);
        }
    };

    if (!data || !data.metrics) return null;

    const { metrics, agingAnalysis, customers } = data;
    const ebitda = data.metrics?.ebitda || { total: 0, net_income: 0, addbacks: [] };

    const agingEntries = Object.entries(agingAnalysis || {}).map(([label, val]) => ({
        label,
        amount: typeof val === 'number' ? val : val.amount,
        avgTrustScore: typeof val === 'number' ? null : val.avgTrustScore,
        pct: metrics.totalAR > 0 ? (((typeof val === 'number' ? val : val.amount) / metrics.totalAR) * 100).toFixed(1) : 0,
    }));

    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const periodLabel = period?.startDate && period?.endDate
        ? `${period.startDate} — ${period.endDate}`
        : 'All Time';

    return (
        <>
            {/* Overlay backdrop */}
            <div
                className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8"
                id="ar-report-print-root"
            >
                {/* Action bar */}
                <div className="no-print fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2 rounded-full bg-zinc-900/95 border border-zinc-700 shadow-2xl">
                    <span className="text-xs font-semibold text-zinc-300 uppercase tracking-widest">AR Financial Report</span>
                    <div className="w-px h-4 bg-zinc-700" />
                    <button
                        onClick={handlePrint}
                        disabled={isGenerating}
                        className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-full transition-all"
                    >
                        {isGenerating ? (
                            <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                            </svg>
                        ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                        )}
                        {isGenerating ? 'Generating...' : 'Download PDF'}
                    </button>
                    <button
                        onClick={onClose}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-full transition-all"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Close
                    </button>
                </div>

                {/* A4 Document */}
                <div
                    className="ar-report-page bg-white text-gray-900 rounded-lg shadow-2xl mt-16"
                    style={{ width: '794px', minHeight: '1123px', fontFamily: "'Inter', 'Roboto', sans-serif", padding: '60px 56px' }}
                >
                    {/* ── HEADER ── */}
                    <div style={{ borderBottom: '2px solid #1e293b', paddingBottom: '20px', marginBottom: '32px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <div style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.5px' }}>
                                    Accounts Receivable Report
                                </div>
                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', fontWeight: '500' }}>
                                    Period: {periodLabel} &nbsp;·&nbsp; Filter: {period?.filterType || 'Created'}
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '11px', color: '#94a3b8' }}>Generated</div>
                                <div style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>{today}</div>
                                <div style={{ marginTop: '6px', fontSize: '10px', color: '#94a3b8', fontStyle: 'italic' }}>Powered by Golag AI</div>
                            </div>
                        </div>
                    </div>

                    {/* ── KEY METRICS ── */}
                    <SectionTitle>Key Metrics</SectionTitle>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '32px' }}>
                        {[
                            { label: 'Total AR Outstanding', value: fmt(metrics.totalAR) },
                            { label: 'Collection Efficiency', value: fmtPct(metrics.collectionEfficiency) },
                            { label: 'Cash Realization', value: fmtPct(metrics.cashRealization) },
                            { label: 'Days Sales Outstanding', value: `${(metrics.daysSalesOutstanding || 0).toFixed(1)}d` },
                            { label: 'Avg Trust Score', value: `${(metrics.avgTrustScore || 0).toFixed(1)}/100` },
                            { label: 'At-Risk Accounts', value: metrics.atRiskAccounts },
                        ].map((m) => (
                            <div key={m.label} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px', background: '#f8fafc' }}>
                                <div style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', marginBottom: '4px' }}>{m.label}</div>
                                <div style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>{m.value}</div>
                            </div>
                        ))}
                    </div>

                    {/* ── AR AGING ANALYSIS ── */}
                    <SectionTitle>AR Aging Analysis</SectionTitle>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '32px', fontSize: '12px' }}>
                        <thead>
                            <tr style={{ background: '#f1f5f9' }}>
                                <Th left>Aging Bucket</Th>
                                <Th>Amount</Th>
                                <Th>% of Total AR</Th>
                                <Th>Avg Trust Score</Th>
                                <Th left>Bar</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {agingEntries.map((row, i) => (
                                <tr key={row.label} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    <td style={{ padding: '8px 10px', fontWeight: '600', color: '#0f172a' }}>{row.label}</td>
                                    <Td mono>{fmt(row.amount)}</Td>
                                    <Td>{row.pct}%</Td>
                                    <Td>{row.avgTrustScore !== null && row.amount > 0 ? Number(row.avgTrustScore).toFixed(1) : '—'}</Td>
                                    <td style={{ padding: '8px 10px' }}>
                                        <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', width: '100%' }}>
                                            <div style={{ height: '8px', borderRadius: '4px', width: `${row.pct}%`, background: AGING_COLORS[row.label] || '#3b82f6', transition: 'width 0.5s' }} />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            <tr style={{ background: '#1e293b' }}>
                                <td style={{ padding: '9px 10px', fontWeight: '800', color: '#f1f5f9', fontSize: '12px' }}>Total AR</td>
                                <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: '800', color: '#f1f5f9', fontFamily: 'monospace' }}>{fmt(metrics.totalAR)}</td>
                                <td colSpan={3} style={{ padding: '9px 10px', textAlign: 'center', color: '#94a3b8', fontSize: '11px' }}>100%</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* ── CUSTOMER OVERVIEW ── */}
                    <SectionTitle>Customer AR Overview</SectionTitle>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '32px', fontSize: '11px' }}>
                        <thead>
                            <tr style={{ background: '#f1f5f9' }}>
                                <Th left>Customer</Th>
                                <Th>Outstanding</Th>
                                <Th>Trust Score</Th>
                                <Th>Terms</Th>
                                <Th>Due Date</Th>
                                <Th>Status</Th>
                                <Th>Last Invoice</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {(customers || []).slice(0, 20).map((c, i) => {
                                const isOverdue = c.status === 'Overdue';
                                return (
                                    <tr key={c.id || i} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        <td style={{ padding: '7px 10px', fontWeight: '600', color: c.isAtRisk ? '#b91c1c' : '#1e40af', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {c.isAtRisk && <span style={{ fontWeight: '800', marginRight: '4px' }}>!</span>}
                                            {c.customer}
                                        </td>
                                        <Td mono>{fmt(c.outstanding)}</Td>
                                        <Td>
                                            <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', background: c.trustScore > 85 ? '#dcfce7' : c.trustScore > 65 ? '#fef9c3' : '#fee2e2', color: c.trustScore > 85 ? '#15803d' : c.trustScore > 65 ? '#a16207' : '#b91c1c' }}>
                                                {(c.trustScore || 0).toFixed(1)}
                                            </span>
                                        </Td>
                                        <Td>{c.terms || '—'}</Td>
                                        <Td>{c.dueDate ? new Date(c.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: '2-digit' }) : '—'}</Td>
                                        <Td>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', background: isOverdue ? '#fee2e2' : '#dcfce7', color: isOverdue ? '#b91c1c' : '#15803d' }}>
                                                    {c.status || '—'}
                                                </span>
                                                {isOverdue && c.overdueCount > 1 && (
                                                    <span
                                                        title={`${c.overdueCount} overdue invoices`}
                                                        style={{ padding: '2px 5px', borderRadius: '4px', fontSize: '9px', fontWeight: '700', background: '#fca5a5', color: '#991b1b', border: '1px solid #fca5a5' }}
                                                    >
                                                        +{c.overdueCount - 1}
                                                    </span>
                                                )}
                                            </div>
                                        </Td>
                                        <Td>{c.lastInvoiceDate ? new Date(c.lastInvoiceDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: '2-digit' }) : '—'}</Td>
                                    </tr>
                                );
                            })}
                            {customers?.length > 20 && (
                                <tr>
                                    <td colSpan={6} style={{ padding: '7px 10px', fontSize: '10px', color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>
                                        + {customers.length - 20} more customers not shown
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* ── EBITDA BREAKDOWN ── */}
                    <SectionTitle>EBITDA Breakdown <span style={{ fontSize: '10px', fontWeight: '400', color: '#94a3b8', marginLeft: '8px' }}>From P&L Report</span></SectionTitle>
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', marginBottom: '32px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <tbody>
                                {/* ── Net Income Breakdown lines ── */}
                                {(ebitda.net_income_breakdown || []).length === 0 && (
                                    <tr>
                                        <td colSpan={2} style={{ padding: '10px 16px', color: '#9ca3af', fontStyle: 'italic', fontSize: '11px' }}>
                                            No P&L breakdown data available for this period.
                                        </td>
                                    </tr>
                                )}
                                {(ebitda.net_income_breakdown || []).map((item, i) => {
                                    const isSubtotal = item.type === 'subtotal';
                                    const isExpense  = item.type === 'expense';
                                    const isIncome   = item.type === 'income';
                                    return (
                                        <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', background: isSubtotal ? '#f1f5f9' : i % 2 === 0 ? '#ffffff' : '#fafafa' }}>
                                            <td style={{ padding: isSubtotal ? '9px 16px' : '7px 16px 7px 28px', color: isSubtotal ? '#374151' : '#6b7280', fontWeight: isSubtotal ? '700' : '400', fontSize: isSubtotal ? '12px' : '11px' }}>
                                                {isSubtotal ? '' : <span style={{ color: isExpense ? '#ef4444' : '#22c55e', marginRight: '8px' }}>{isExpense ? '−' : '+'}</span>}
                                                {item.name}
                                            </td>
                                            <td style={{ padding: isSubtotal ? '9px 16px' : '7px 16px', textAlign: 'right', fontFamily: 'monospace', fontWeight: isSubtotal ? '700' : '400', color: isExpense ? '#b91c1c' : isIncome ? '#15803d' : '#374151', fontSize: isSubtotal ? '12px' : '11px' }}>
                                                {isExpense ? `(${fmt(Math.abs(item.amount))})` : fmt(Math.abs(item.amount))}
                                            </td>
                                        </tr>
                                    );
                                })}

                                {/* ── Net Income total row ── */}
                                <tr style={{ background: '#f0fdf4', borderBottom: '2px solid #e2e8f0' }}>
                                    <td style={{ padding: '10px 16px', color: '#374151', fontWeight: '700', fontSize: '12px' }}>Net Income</td>
                                    <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'monospace', fontWeight: '800', fontSize: '13px', color: ebitda.net_income >= 0 ? '#15803d' : '#b91c1c' }}>
                                        {fmt(ebitda.net_income)}
                                    </td>
                                </tr>

                                {/* ── EBITDA Add-backs ── */}
                                {(ebitda.addbacks || []).length === 0 && (
                                    <tr>
                                        <td colSpan={2} style={{ padding: '10px 16px', color: '#9ca3af', fontStyle: 'italic', fontSize: '11px' }}>
                                            No depreciation / amortization / interest accounts identified in this period.
                                        </td>
                                    </tr>
                                )}
                                {(ebitda.addbacks || []).map((ab, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                        <td style={{ padding: '9px 16px 9px 28px', color: '#6b7280', fontSize: '11px' }}>
                                            <span style={{ color: '#9ca3af', marginRight: '8px' }}>+</span>
                                            {ab.name}
                                        </td>
                                        <td style={{ padding: '9px 16px', textAlign: 'right', fontFamily: 'monospace', color: '#374151', fontSize: '11px' }}>{fmt(ab.amount)}</td>
                                    </tr>
                                ))}


                                {/* Divider */}
                                <tr>
                                    <td colSpan={2} style={{ padding: 0 }}>
                                        <div style={{ height: '2px', background: '#1e293b', margin: '0' }} />
                                    </td>
                                </tr>
                                <tr style={{ background: '#1e293b' }}>
                                    <td style={{ padding: '12px 16px', fontWeight: '800', fontSize: '14px', color: '#f1f5f9', letterSpacing: '-0.3px' }}>EBITDA Total</td>
                                    <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'monospace', fontWeight: '800', fontSize: '18px', color: ebitda.total >= 0 ? '#4ade80' : '#f87171' }}>
                                        {fmt(ebitda.total)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>


                    {/* ── FOOTER ── */}
                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                            This report is generated from QuickBooks Online data via Golag AI.
                        </div>
                        <div style={{ fontSize: '10px', color: '#94a3b8', fontFamily: 'monospace' }}>
                            {today}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

/* ── Small helper components for table cells ── */
const SectionTitle = ({ children }) => (
    <div style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748b', marginBottom: '10px', paddingBottom: '4px', borderBottom: '1px solid #e2e8f0' }}>
        {children}
    </div>
);

const Th = ({ children, left }) => (
    <th style={{ padding: '8px 10px', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#64748b', textAlign: left ? 'left' : 'right', background: '#f1f5f9', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
        {children}
    </th>
);

const Td = ({ children, mono }) => (
    <td style={{ padding: '7px 10px', textAlign: 'right', color: '#374151', fontFamily: mono ? 'monospace' : 'inherit', whiteSpace: 'nowrap' }}>
        {children}
    </td>
);

export default ArReportDocument;
